import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Search, User, Camera, LogOut } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, cart, logout, setAuthOpen, setVisualOpen } = useApp();
  const loc = useLocation();
  const cartCount = cart.items.reduce((s, i) => s + i.quantity, 0);

  const linkCls = (path) =>
    `font-mono-brand text-xs uppercase tracking-[0.2em] transition-colors ${
      loc.pathname === path ? "text-black" : "text-neutral-500 hover:text-black"
    }`;

  return (
    <header
      className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5"
      data-testid="nova-navbar"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-8">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-2.5 h-2.5 bg-[#FF3B30] rotate-45" />
          <span className="font-display font-black text-xl tracking-tighter">NOVA/MARKET</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <Link to="/shop/all" className={linkCls("/shop/all")} data-testid="nav-shop-all">All</Link>
          <Link to="/shop/fashion" className={linkCls("/shop/fashion")} data-testid="nav-shop-fashion">Fashion</Link>
          <Link to="/shop/home" className={linkCls("/shop/home")} data-testid="nav-shop-home">Home</Link>
          <Link to="/shop/electronics" className={linkCls("/shop/electronics")} data-testid="nav-shop-electronics">Electronics</Link>
        </nav>

        <div className="flex items-center gap-1 md:gap-3">
          <Link to="/shop/all" aria-label="Search">
            <Button variant="ghost" size="icon" className="hover:bg-black/5" data-testid="nav-search-btn">
              <Search className="w-4 h-4" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setVisualOpen(true)}
            className="hover:bg-[#FF3B30]/10 text-[#FF3B30]"
            data-testid="nav-visual-search-btn"
            title="Visual search"
          >
            <Camera className="w-4 h-4" />
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-black/5" data-testid="nav-user-btn">
                  <User className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border border-black/10 min-w-[200px]">
                <div className="px-3 py-2 text-xs font-mono-brand uppercase tracking-widest text-neutral-500">
                  {user.name}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-orders">
                  <Link to="/orders">Orders</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-3 h-3 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              onClick={() => setAuthOpen(true)}
              className="hover:bg-black/5 font-mono-brand text-xs uppercase tracking-[0.2em]"
              data-testid="nav-signin-btn"
            >
              Sign in
            </Button>
          )}

          <Link to="/cart" className="relative" data-testid="nav-cart-btn">
            <Button variant="ghost" size="icon" className="hover:bg-black/5">
              <ShoppingBag className="w-4 h-4" />
            </Button>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-[10px] font-mono-brand w-4 h-4 flex items-center justify-center rounded-full">
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}
